/* eslint-disable prettier/prettier */
/* eslint-disable no-shadow */
import React from 'react';

import { InterestPointType, InterestPointTypeValues } from 'models/repossession/enums/repossession.enums';
import { CircleLayer, HeatmapLayer } from 'mapbox-gl';
import { Marker } from 'react-map-gl';
import Supercluster from 'supercluster';
import { Typography } from 'design_system/src';
import { momentLocal } from 'models/shared/helpers/dates';
import { GPSCommands } from 'models/geozon/enums/gps.enum';
import { GeotetTileResponseDto } from 'models/_external/geotile/interfaces/geotile.interface';

export interface DeviceLocation extends GeotetTileResponseDto {
	locationType:InterestPointType,
}

const HEATMAP_MAX_ZOOM_LEVEL = 15;

export const hotspotHeatLayerDefinition: HeatmapLayer = {
	id: 'interest_points_heat',
	type: 'heatmap',
	maxzoom: HEATMAP_MAX_ZOOM_LEVEL,
	paint: {
		// increase weight as diameter breast height increases
		'heatmap-weight': [
			'interpolate',
			['linear'],
			['get', 'weight'],
			0,
			0.01,
			1,
			1,
		],
		// increase intensity as zoom level increases
		'heatmap-intensity': [
			'interpolate',
			['linear'],
			['zoom'],
			0,
			1,
			HEATMAP_MAX_ZOOM_LEVEL,
			3,
		],
		// assign color values be applied to points depending on their density
		'heatmap-color': [
			'interpolate',
			['exponential', 30],
			['heatmap-density'],
			0,
			'rgba(111, 157, 236,0)',
			0.5,
			'rgba(111, 157, 236, 0.9)',
			0.8,
			'rgba(236, 237, 239, 0.9)',
			0.85,
			'rgba(255, 216, 171, 0.9)',
			0.9,
			'rgba(223, 32, 64, 0.7)',

			1,
			'rgba(254, 138, 2, 0.9)',
		],
		// Adjust the heatmap radius by zoom level
		'heatmap-radius': [
			'interpolate',
			['linear'],
			['zoom'],
			0,
			20,
			HEATMAP_MAX_ZOOM_LEVEL,
			40,
		],
		// Transition from heatmap to circle layer by zoom level
		'heatmap-opacity': [
			'interpolate',
			['linear'],
			['zoom'],
			HEATMAP_MAX_ZOOM_LEVEL - 2,
			1,
			HEATMAP_MAX_ZOOM_LEVEL,
			0,
		],
	},
};

export const hotspotLayerDefinition: CircleLayer = {
	id: 'interest_points_circles',
	type: 'circle',
	minzoom: HEATMAP_MAX_ZOOM_LEVEL-2,
	paint: {
		'circle-radius': 5,
		'circle-color':'rgb(0,0,0)',
		'circle-stroke-color': 'white',
		'circle-stroke-width': 1,
		'circle-opacity': [
				'interpolate',
				['linear'],
				['zoom'],
				HEATMAP_MAX_ZOOM_LEVEL - 2,
				0,
				HEATMAP_MAX_ZOOM_LEVEL,
				1,
			],
	},
};

export const createClusterMarker = (
	cluster: Supercluster.ClusterFeature<Supercluster.AnyProps> | Supercluster.PointFeature<Supercluster.AnyProps>,
	callback: Function,
) => {
	const [longitude, latitude] = cluster.geometry.coordinates;

	return (
		<Marker
			key={`cluster-${cluster.id}`}
			longitude={longitude}
			latitude={latitude}
			onClick={() => callback(cluster)}
		>
			<div className="cluster_1">
				<div className="cluster_2">
					<div className="cluster_3 dim_xxxl">
						<Typography weight="600" scale="xsmall" className="cluster_count">
							{cluster.properties.point_count}
						</Typography>
					</div>
				</div>
			</div>
		</Marker>
	);
};

export const createPointMarker = (
	cluster: Supercluster.ClusterFeature<Supercluster.AnyProps> | Supercluster.PointFeature<Supercluster.AnyProps>,
	point: DeviceLocation,
	selected:boolean,
	open:boolean,
	visitCount:number,
	callback: Function,
) => {
	const [longitude, latitude] = cluster.geometry.coordinates;
	const Icon = InterestPointTypeValues[point.locationType].icon;

	return (
		<Marker
			key={point._id}
			longitude={longitude}
			latitude={latitude}
			onClick={() => {
				callback(point);
			}}
		>
			<div
				className={`br_xxl main_marker cursor_pointer ${
					InterestPointTypeValues[point.locationType].color
				} ${selected ? 'selected_marker' : ''} ${
					open ? 'open_marker' : ''
				}`}
			>
				<div className="p_y_xxs p_x_md flex_center z_index_1 pos_relative">
					<Icon className="dim_lg icon" />
					<div className="p_x_md flex_center_col">
						<Typography weight="600" scale="xxsmall">
							{momentLocal(point.createdAt).format('DD/MM/YYYY')}
						</Typography>
						<Typography weight="400" scale="xxsmall">
							{momentLocal(point.createdAt).format('HH:mm')}
						</Typography>
					</div>
				</div>
				{visitCount ? (
					<div className="pos_absolute br_circle dim_md marker_counter bg_red_300">
						<Typography
							weight="400"
							scale="xxsmall"
							className="text_neutral_0 pos_absolute center"
						>
							{visitCount}
						</Typography>
					</div>
				) : null}
				<span className="arrow" />
			</div>
		</Marker>
	);
};

export const createClusterHotspotMarker = (
	cluster: Supercluster.ClusterFeature<Supercluster.AnyProps> | Supercluster.PointFeature<Supercluster.AnyProps>,
	callback: Function,
	point: DeviceLocation
) => {
	const [longitude, latitude] = cluster.geometry.coordinates;
	const size = Math.min(cluster.properties.point_count*4/100,4);

	return (
		<Marker
			key={`cluster-${cluster.id}`}
			longitude={longitude}
			latitude={latitude}
			onClick={() => callback(point, true)}
		>
					<div className="bg_neutral_900 br_circle cursor_pointer" style= {{height:`${size}rem`, width:`${size}rem`}}>
						<Typography weight="600" scale="xxsmall" textColor="neutral_0" className="center pos_absolute">
							{size<1?'':cluster.properties.point_count}
						</Typography>
					</div>
		</Marker>
	);
};

export const deviceCommandsDescriptions = [
	{
		title:'Arm',
		subtitle: 'Activar alarma de la moto',
		command: GPSCommands.SET_ARM,
	},
	{
		title:'Disarm',
		subtitle: 'Hacer sonar la moto',
		command: GPSCommands.SET_DISARM,
	},
	{
		title:'Resume power',
		subtitle: 'Prender la moto',
		command: GPSCommands.RESUME_POWER,
	},
	{
		title:'Cut power',
		subtitle: 'Apaga la moto',
		command: GPSCommands.CUT_POWER,
	}
];